import { useState } from 'react';
import { addBundle } from '../../../locale/createUseTextLocalizer';
import { Localizer, getTextLocalizer } from '../../../locale/useTextLocalizer';
import { validateTextInput } from '../useInputValidationAdapter';
import { InputChangeEvent, TextValidationProps, MessageProps } from '../types';
import { BaseInputProps } from '../__internal__/BaseInput';
import { OmitStrict } from '../../../utility-types';
import bundle from '../NovaComponents-Input-gui-icu.properties';
import {
  urlRegex,
  ipAddressRegex,
  isIntegerRegex,
  consecutiveDotsRegex,
  liveRegex,
  countPattern,
} from '../../../util';
import { stripProtocolFromUrl } from '../utils';
addBundle(bundle);
let localize: Localizer;
getTextLocalizer('NovaComponents-Input-gui-icu').then((localizer) => {
  localize = localizer;
});
/**
 * URL Validation
 */
export interface BaseURLValidationProps extends TextValidationProps {
  urlRegex?: string;
  isLive?: boolean;
  liveURLRegex?: string;
  message?: string;
}
export const validateURLInput = (props: BaseURLValidationProps) => {
  let isError = false,
    status: BaseInputProps['status'] = undefined,
    message: string | MessageProps | null | undefined = undefined;
  const colonIndex = props.value.lastIndexOf(':');
  const endOfPortNumberIndex =
    props.value.slice(colonIndex).indexOf('/') !== -1
      ? props.value.slice(colonIndex).indexOf('/') + colonIndex
      : props.value.length;
  const urlWithoutProtocol = stripProtocolFromUrl(props.value);
  const result = validateTextInput({
    value: props.value,
    isRequired: props.isRequired,
    minLength: props.minLength,
  });
  if (result.isError) {
    isError = result.isError;
    status = result.status;
    message = result.message;
  } else {
    if (props.value) {
      if (!props.isLive) {
        if (
          stripProtocolFromUrl(props.value).length === props.value.length &&
          !urlRegex(props.value) &&
          !ipAddressRegex(props.value)
        ) {
          isError = true;
          status = 'alert';
          message = props.message || localize('urlValidator.msg');
        }
        if (
          stripProtocolFromUrl(props.value).length < props.value.length &&
          !urlRegex(stripProtocolFromUrl(props.value)) &&
          !ipAddressRegex(props.value)
        ) {
          isError = true;
          status = 'alert';
          message = props.message || localize('urlValidator.msg');
        }
        const colonIndexFromStrippedUrl = urlWithoutProtocol.indexOf(':');
        let portNumber = props.value.slice(colonIndex + 1, endOfPortNumberIndex);
        portNumber = colonIndexFromStrippedUrl >= 0 && isIntegerRegex(portNumber) ? portNumber : '';
        if (
          (portNumber !== '' &&
            (parseInt(portNumber) > 99999 ||
              (urlWithoutProtocol.lastIndexOf('/') >= 0 &&
                urlWithoutProtocol.lastIndexOf('/') < colonIndexFromStrippedUrl))) ||
          (colonIndexFromStrippedUrl >= 0 && portNumber === '') ||
          countPattern(urlWithoutProtocol, ':') > 1
        ) {
          isError = true;
          status = 'alert';
          message = props.message || localize('urlValidator.msg');
        }
      } else {
        if (!liveRegex(props.value) && !ipAddressRegex(props.value)) {
          isError = true;
          status = 'alert';
          message = props.message || localize('urlValidator.msg');
        }
      }
      if (!consecutiveDotsRegex(props.value)) {
        isError = true;
        status = 'alert';
        message = localize('emailValidatorConsecutivePeriods.msg');
      }
    }
  }
  return { isError, status, message };
};
export interface URLValidationProps extends OmitStrict<BaseURLValidationProps, 'value'> {}
/**
 * This hook provide functionality to handle events such as onChange,onBlur
 * & onCancel with empty initial value.
 * For those who doesn't wish to use this,
 * @function validateURLInput is available for consumption
 * @param props
 *
 */
export const useURLValidationAdapter = (props: URLValidationProps) => {
  const [value, setValue] = useState('');
  const [errors, setErrors] = useState<{
    message: string | MessageProps | null | undefined;
    status: BaseInputProps['status'];
  }>({ status: undefined, message: '' });
  const handleChange = (event: InputChangeEvent) => {
    const liveValue = event.value;
    const result = validateURLInput({
      value: liveValue,
      isLive: true,
      isRequired: props.isRequired,
      message: props.message,
      urlRegex: props.urlRegex,
      liveURLRegex: props.liveURLRegex,
      minLength: props.minLength,
    });
    setValue(liveValue);
    result.isError
      ? setErrors({ status: result.status, message: result.message })
      : setErrors({ status: undefined, message: undefined });
  };
  const handleBlur = () => {
    const result = validateURLInput({
      value,
      isLive: false,
      isRequired: props.isRequired,
      message: props.message,
      urlRegex: props.urlRegex,
      liveURLRegex: props.liveURLRegex,
      minLength: props.minLength,
    });
    result.isError && setErrors({ status: result.status, message: result.message });
  };
  const handleCommit = handleBlur;
  const handleCancel = () => {
    setValue('');
    setErrors({ status: undefined, message: undefined });
  };
  return { handleChange, value, errors, handleBlur, handleCancel, handleCommit };
};
